import { isDefined } from "@clipboard-health/util-ts";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ON_TIME_BONUS_GRACE_PERIOD_MINUTES } from "@src/appV2/ClipboardScore/constants";
import { useDiscardLatenessBooktimeThreshold } from "@src/appV2/ClipboardScore/hooks/featureFlags";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { useGetWorkerTrips } from "@src/appV2/redesign/Shift/WorkerEta/useGetWorkerTrips";
import { findCancellationPolicy } from "@src/appV2/redesign/WorkerShifts/ShiftCancel/utils/findCancellationPolicy";
import { findLatenessConfigBasedOnMinutesLate } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/utils/getLatenessConfig";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { addMinutes, differenceInHours, differenceInMinutes, parseISO } from "date-fns";
import { useMemo } from "react";

const ETA_TRACKING_PROXIMITY_HOURS = 2;
const CURRENT_DATE_UPDATE_INTERVAL_MS = 60_000;

interface UseScoreImpactParams {
  readonly shiftId: string;
  readonly shiftStartIso: string;
  readonly qualification: string | undefined;
  readonly workplaceId: string;
  readonly targetArrivalTime: string | undefined;
  readonly bookedAt: string | undefined;
  readonly enabled?: boolean;
}

interface ScoreImpactResult {
  readonly isLoading: boolean;
  readonly currentScore: number | undefined;
  readonly penaltyPoints: number | undefined;
  readonly scoreAfterCancel: number | undefined;
  readonly wouldRestrict: boolean;
  readonly etaTime: Date | undefined;
  readonly latenessMessage: string | undefined;
  readonly timelinessMessage: string | undefined;
  readonly shouldShowEtaImpact: boolean;
  readonly isShiftApproaching: boolean;
}

export type { ScoreImpactResult };

export function useScoreImpact(params: UseScoreImpactParams): ScoreImpactResult {
  const {
    shiftId,
    shiftStartIso,
    qualification,
    workplaceId,
    targetArrivalTime,
    bookedAt,
    enabled = true,
  } = params;
  const { userId } = useDefinedWorker();
  const now = useCurrentDate(CURRENT_DATE_UPDATE_INTERVAL_MS);
  const { discardLatenessBooktimeThresholdInHours } = useDiscardLatenessBooktimeThreshold();

  const { data: profile, isLoading: isProfileLoading } = useGetClipboardScoreProfile(
    userId,
    { enabled: isDefined(userId) && enabled },
    qualification,
    workplaceId
  );

  const { data: trips, isLoading: isTripsLoading } = useGetWorkerTrips({ shiftId }, { enabled });

  const isLoading = isProfileLoading || isTripsLoading;

  return useMemo((): ScoreImpactResult => {
    if (!enabled || isLoading || !isDefined(profile)) {
      return {
        isLoading: enabled && isLoading,
        currentScore: undefined,
        penaltyPoints: undefined,
        scoreAfterCancel: undefined,
        wouldRestrict: false,
        etaTime: undefined,
        latenessMessage: undefined,
        timelinessMessage: undefined,
        shouldShowEtaImpact: false,
        isShiftApproaching: false,
      };
    }

    const shiftStart = parseISO(shiftStartIso);
    const leadTimeHours = Math.max(0, differenceInMinutes(shiftStart, now) / 60);

    // Cancellation
    const cancelPolicy = findCancellationPolicy(profile.policy?.cancelShift, leadTimeHours);
    const penaltyPoints = cancelPolicy?.points;
    const { clipboardScore: currentScore } = profile;
    const scoreAfterCancel =
      isDefined(penaltyPoints) && isDefined(currentScore)
        ? currentScore + penaltyPoints
        : undefined;
    const wouldRestrict = isDefined(scoreAfterCancel) && scoreAfterCancel <= 0;

    // ETA + lateness/timeliness
    const trip = trips?.find((t) => t.attributes.shiftId === shiftId);
    const etaDurationMinutes = trip?.attributes.currentEtaDuration;
    const etaTime = isDefined(etaDurationMinutes) ? addMinutes(now, etaDurationMinutes) : undefined;

    // Determine if lateness penalties are discarded for last-minute bookings without TAT
    const isLastMinuteBooking =
      isDefined(bookedAt) &&
      differenceInHours(shiftStart, parseISO(bookedAt), { roundingMethod: "ceil" }) <=
        discardLatenessBooktimeThresholdInHours;
    const shouldShowEtaImpact = !(isLastMinuteBooking && !isDefined(targetArrivalTime));

    // Use target arrival time as lateness deadline when available (short-lead-time bookings)
    const arrivalDeadline = isDefined(targetArrivalTime) ? parseISO(targetArrivalTime) : shiftStart;

    let latenessMessage: string | undefined;
    let timelinessMessage: string | undefined;

    if (
      shouldShowEtaImpact &&
      isDefined(etaDurationMinutes) &&
      isDefined(profile.policy?.latenessConfig)
    ) {
      const minutesToDeadline = differenceInMinutes(arrivalDeadline, now, {
        roundingMethod: "round",
      });
      const minutesLate = Math.max(0, etaDurationMinutes - minutesToDeadline);

      if (minutesLate > ON_TIME_BONUS_GRACE_PERIOD_MINUTES) {
        const latenessConfig = findLatenessConfigBasedOnMinutesLate(
          minutesLate,
          profile.policy.latenessConfig
        );
        if (isDefined(latenessConfig)) {
          latenessMessage = `Based on your ETA, arriving ${String(Math.round(minutesLate))} minutes late will deduct ${String(Math.abs(latenessConfig.points))} points from your score.`;
        }
      } else {
        const bonusPoints = profile.policy?.shiftClockInConfig?.points;
        if (isDefined(bonusPoints) && bonusPoints > 0) {
          timelinessMessage = `Based on your ETA, you'll earn ${String(bonusPoints)} points for arriving on time.`;
        }
      }
    }

    return {
      isLoading: false,
      currentScore,
      penaltyPoints,
      scoreAfterCancel,
      wouldRestrict,
      etaTime,
      latenessMessage,
      timelinessMessage,
      shouldShowEtaImpact,
      isShiftApproaching: leadTimeHours <= ETA_TRACKING_PROXIMITY_HOURS,
    };
  }, [
    shiftStartIso,
    shiftId,
    profile,
    trips,
    isLoading,
    enabled,
    now,
    targetArrivalTime,
    bookedAt,
    discardLatenessBooktimeThresholdInHours,
  ]);
}
